<template>
	<app-header-teleport>
		<app-title :title="$t('reviews')" />
		<div class="ml-2">
			<app-advanced-filter @onFilter="refresh" @onClear="onClear" :loading="refreshing">
				<app-advanced-date-filter v-model="filters" :start-date="'startDate'" :end-date="'endDate'" />
				<app-select
					class="mr-1 w-48"
					placeholder="Business"
					:can-deselect="false"
					:options="restaurantOptions"
					v-model="selectedRestaurant"
				/>
			</app-advanced-filter>
		</div>
	</app-header-teleport>
	<app-group-card-filter v-model="filters.ratesNo" :filters="statusFilters" :statistics="statistics" />
	<app-section class="flex flex-wrap justify-items-start gap-15">
		<app-rates-card :statistics="reviewStatistics" title="Total" />
		<app-rates-card :statistics="statistics" :title="`Period: ${filters.startDate} - ${filters.endDate}`" />
	</app-section>
	<app-section class="tight overflow-x-auto">
		<div class="flex mb-6">
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2"
				:class="isChosenType('border-primary', 'border-border-color')"
				@click="onReviewSelect(1)"
			>
				{{ $t('foodReviews') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2"
				:class="isChosenType('border-border-color', 'border-primary')"
				@click="onReviewSelect(2)"
			>
				{{ $t('transportReviews') }}
			</div>
		</div>
		<app-input
			name="Comment Search"
			placeholder="Search by comment"
			:modelValue="filters.userSearch"
			@update:modelValue="(val) => updateFilter('userSearch', val)"
			:debounceInterval="500"
		/>
		<app-table v-if="chosenType === 1" :columns="columns" :data="reviews" :searchable="false">
			<template v-slot:phone="{ item }">
				<span
					class="text-info cursor-pointer"
					@click="showFullNumber(item.restaurantID, item.id, item.count)"
					>{{ item.phone }}</span
				>
			</template>
			<template v-slot:registrationtime="{ item }">
				{{ $filters.getTimeInFormat(item['registrationtime'], 'MMM') }}
				{{ $filters.getTimeInFormat(item['registrationtime'], 'yyyy') }}
			</template>
		</app-table>
		<app-table v-if="chosenType === 2" :columns="columns" :data="reviews" :searchable="false">
			<template v-slot:restaurantname="{ item }">
				<span>{{ item.businessName }}</span>
			</template>
			<template v-slot:comment="{ item }">
				<span>{{ item.Comment }}</span>
			</template>
			<template v-slot:count="{ item }">
				<span>{{ item.Count }}</span>
			</template>
			<template v-slot:phone="{ item }">
				<span class="text-info">{{ item.phone }}</span>
			</template>
			<template v-slot:registrationtime="{ item }">
				{{ $filters.getTimeInFormat(item['CDate'], 'MMM') }}
				{{ $filters.getTimeInFormat(item['CDate'], 'yyyy') }}
			</template>
		</app-table>
		<app-pagination
			:filters="filters"
			@prevPage="prevPage"
			@nextPage="nextPage"
			@limitChange="changeLimit"
			:loading="refreshing"
		/>
	</app-section>
</template>

<script>
	import RouteNames from '@/router/names';
	import constants from '@/constants/reviews';
	import AppTable from '@/components/app/AppTable';
	import AppGroupCardFilter from '@/components/app/AppGroupCardFilter';
	import { calcProgressPercentage } from '@/services/UtilityService';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppPagination from '@/components/app/AppPagination';
	import AppInput from '@/components/app/AppInput';
	import { mapGetters } from 'vuex';
	import DashboardService from '@/services/DashboardService';
	import OrderService from '@/services/OrderService';
	import AppRatesCard from '@/components/app/AppRatesCard';
	import moment from 'moment';
	import AppSelect from '@/components/app/AppSelect';

	export default {
		name: 'ReviewsList',
		components: {
			AppSelect,
			AppRatesCard,
			AppTable,
			AppGroupCardFilter,
			AppAdvancedFilter,
			AppAdvancedDateFilter,
			AppPagination,
			AppInput,
		},
		data: () => ({
			RouteNames,
			loaded: false,
			refreshing: false,
			filters: { ...constants.defaultFilters },
			columns: constants.listColumns,
			reviews: [],
			statistics: {},
			reviewStatistics: {},
			statusFilters: constants.status,
			deletingReviewId: null,
			chosenType: 1,
			selectedRestaurant: null,
			lastExecutionTime: 0,
		}),
		computed: {
			...mapGetters({
				user: 'user',
			}),
			restaurantOptions() {
				let array = [];
				this.user.restaurants.forEach((res) =>
					array.push({ label: res.restaurantName, value: res.restaurantID })
				);
				return array;
			},
		},
		methods: {
			showFullNumber(restaurantId, reviewId, count) {
				const currentTime = Date.now();
				if (currentTime - this.lastExecutionTime < 1500) {
					return;
				}
				this.lastExecutionTime = currentTime;
				this.refreshing = true;
				DashboardService.getReviewData(restaurantId, reviewId, { count })
					.then((response) => {
						this.reviews = this.reviews.filter((item) => {
							if (item.id === response.id) {
								item.phone = response.phone;
							}
							return item;
						});
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.refreshing = false;
					});
			},
			getSelectedRestaurant() {
				this.selectedRestaurant = this.user.restaurants[0].restaurantID;
			},
			onReviewSelect(val) {
				this.chosenType = val;
			},
			isChosenType(val1, val2) {
				return this.chosenType === 1 ? val1 : val2;
			},
			formatOrderId(id) {
				return OrderService.formatOrderId(id);
			},
			calcProgressPercentage,
			getReviewStatistics() {
				DashboardService.get({
					reportTypeId: this.chosenType === 1 ? 11 : 18,
					startDate: moment()
						.subtract(1, 'year')
						.format('YYYY-MM-DD'),
					endDate: moment().format('YYYY-MM-DD'),
					businessIDs: this.selectedRestaurant,
				}).then((statistics) => {
					this.reviewStatistics = statistics;
					this.reviewStatistics.rateAll =
						this.reviewStatistics.rate1 +
						this.reviewStatistics.rate2 +
						this.reviewStatistics.rate3 +
						this.reviewStatistics.rate4 +
						this.reviewStatistics.rate5 +
						this.reviewStatistics.rate6 +
						this.reviewStatistics.rate7 +
						this.reviewStatistics.rate8 +
						this.reviewStatistics.rate9 +
						this.reviewStatistics.rate10;
				});
			},
			setTextValues() {
				let firstRate;
				let secondRate;
				this.statusFilters.map((filter) => {
					if (filter.propKey === 'rateAll') {
						filter.text = this.statistics[filter.propKey];
						filter.comparison = this.calcProgressPercentage(
							this.statistics[filter.propKey],
							this.statistics.compared[filter.propKey]
						);
					} else {
						if (filter.propKey === 'rate5') {
							firstRate = 'rate10';
							secondRate = 'rate9';
						} else if (filter.propKey === 'rate4') {
							firstRate = 'rate8';
							secondRate = 'rate7';
						} else if (filter.propKey === 'rate3') {
							firstRate = 'rate6';
							secondRate = 'rate5';
						} else if (filter.propKey === 'rate2') {
							firstRate = 'rate4';
							secondRate = 'rate3';
						} else if (filter.propKey === 'rate1') {
							firstRate = 'rate2';
							secondRate = 'rate1';
						}
						filter.text = this.statistics[firstRate] + this.statistics[secondRate];
						filter.comparison = this.calcProgressPercentage(
							this.statistics[firstRate] + this.statistics[secondRate],
							this.statistics.compared[firstRate] + this.statistics.compared[secondRate]
						);
					}
				});
			},
			getReviews() {
				this.refreshing = true;
				DashboardService.getReviews(this.selectedRestaurant, {
					...this.filters,
					reportTypeId: this.chosenType === 1 ? 12 : 19,
				})
					.then((reviews) => {
						this.reviews = reviews;
					})
					.finally(() => {
						this.refreshing = false;
						this.emitter.emit('isLoading', false);
					});
			},
			getStatistics() {
				DashboardService.get({
					reportTypeId: this.chosenType === 1 ? 11 : 18,
					businessIDs: this.selectedRestaurant,
					startDate: this.filters.startDate,
					endDate: this.filters.endDate,
				}).then((statistics) => {
					this.statistics = statistics;
					this.statistics.rateAll =
						this.statistics.rate1 +
						this.statistics.rate2 +
						this.statistics.rate3 +
						this.statistics.rate4 +
						this.statistics.rate5 +
						this.statistics.rate6 +
						this.statistics.rate7 +
						this.statistics.rate8 +
						this.statistics.rate9 +
						this.statistics.rate10;
					this.setTextValues();
				});
			},
			updateFilter(key, val) {
				if (val.length > 3 || val.length === 0) {
					this.filters[key] = val;
					this.filters.offset = 0;
					this.getReviews();
				}
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.refresh();
			},
			refresh() {
				this.getReviews();
				this.getStatistics();
			},
			prevPage() {
				this.filters.offset = Math.max(this.filters.offset - this.filters.limit, 0);
				this.getReviews();
			},
			nextPage() {
				this.filters.offset = this.filters.offset + this.filters.limit;
				this.getReviews();
			},
			changeLimit(value) {
				this.filters = {
					...this.filters,
					limit: value,
					offset: 0,
				};
				this.getReviews();
			},
		},
		created() {
			this.getSelectedRestaurant();
		},
		mounted() {
			this.refresh();
		},
		watch: {
			chosenType() {
				this.filters.offset = 0;
				this.refresh();
				this.getReviewStatistics();
			},
			'filters.ratesNo'() {
				this.filters.offset = 0;
				this.getReviews();
			},
			selectedRestaurant() {
				this.getReviewStatistics();
			},
		},
	};
</script>
